/** Docs (UA): see modules/game/DOCS.md — one try per purchase, local record */

import { useCallback, useState } from 'react';
import {
    GAME_RECORD_STORAGE_KEY,
    GAME_SOUND_STORAGE_KEY,
    gameTryStorageKey,
} from '../game.constants';

const safeGet = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const safeSet = (key: string, value: string): void => {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Private mode etc. — the try then lives only for this page view,
        // which still respects the "one try" frame.
    }
};

export interface GameTry {
    isTryAvailable: boolean;
    markTryUsed: () => void;
    record: number;
    saveRecord: (score: number) => boolean;
    soundEnabled: boolean;
    setSoundEnabled: (on: boolean) => void;
}

/** The try burns the moment the game STARTS (interrupted session burns too —
 *  design frame). Keyed by order id: one purchase = one try, ever. */
export const useGameTry = (orderId: number | string): GameTry => {
    const tryKey = gameTryStorageKey(orderId);
    const [isTryAvailable, setTryAvailable] = useState(() => safeGet(tryKey) === null);
    const [record, setRecord] = useState(() => Number(safeGet(GAME_RECORD_STORAGE_KEY) ?? 0));
    const [soundEnabled, setSoundState] = useState(() => safeGet(GAME_SOUND_STORAGE_KEY) === '1');

    const markTryUsed = useCallback((): void => {
        safeSet(tryKey, 'used');
        setTryAvailable(false);
    }, [tryKey]);

    const saveRecord = useCallback(
        (score: number): boolean => {
            if (score <= record) return false;
            safeSet(GAME_RECORD_STORAGE_KEY, String(score));
            setRecord(score);
            return true;
        },
        [record],
    );

    const setSoundEnabled = useCallback((on: boolean): void => {
        safeSet(GAME_SOUND_STORAGE_KEY, on ? '1' : '0');
        setSoundState(on);
    }, []);

    return { isTryAvailable, markTryUsed, record, saveRecord, soundEnabled, setSoundEnabled };
};
